import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getGym, updateGym } from '../../services/firebase/gym';
import { getClimber } from '../../services/firebase/climber';
import { getClimbingRoutes } from '../../services/firebase/climbingRoute';
import { Gym } from '../../models/gym/Gym';
import { ClimbingRoute } from '../../models/climbing/ClimbingRoute';
import HamburgerMenu from '../../components/HamburgerMenu';
import '../../styles/GymEdit.css';
import '../../styles/MyGyms.css';

const MyGymDetail: React.FC = () => {
  const { gymId } = useParams<{ gymId: string }>();
  const [gym, setGym] = useState<Gym | null>(null);
  const [routes, setRoutes] = useState<ClimbingRoute[]>([]);
  const [gymName, setGymName] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [styles, setStyles] = useState<string[]>([]);
  
  // Available gym styles
  const availableStyles = ['Bouldering', 'Rope climbing'];
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [hasEditAccess, setHasEditAccess] = useState(false);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchGymAndCheckAccess = async () => {
      if (!gymId || !currentUser) {
        setError('Missing gym ID or user not logged in');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Fetch gym details
        const gymData = await getGym(gymId);
        if (!gymData) {
          setError('Gym not found');
          setLoading(false);
          return;
        }
        setGym(gymData);
        
        // Set form values
        setGymName(gymData.name || '');
        setImageUrl(gymData.imageUrl || '');
        setLogoUrl(gymData.logoUrl || '');
        setStyles(gymData.styles || []);
        
        // Check if user has edit access (owner or fre role)
        const climber = await getClimber(currentUser.uid);
        if (climber) {
          const role = climber.getRoleInGym(gymId);
          if (role !== 'owner' && role !== 'fre') {
            // Navigate away if user doesn't have appropriate role
            navigate('/gyms');
            return;
          }
          setHasEditAccess(true);
        } else {
          navigate('/gyms');
          return;
        }
        
        // Fetch climbing routes for this gym
        const routesData = await getClimbingRoutes(gymId);
        setRoutes(routesData);
        
        setError('');
      } catch (err: any) {
        console.error('Error fetching gym data:', err);
        setError('Failed to load gym data');
      } finally {
        setLoading(false);
      }
    };

    fetchGymAndCheckAccess();
  }, [currentUser, gymId, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!gymId || !gym) {
      setError('Gym data not available');
      return;
    }
    
    if (!gymName.trim()) {
      setError('Gym name is required');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      // Create updated gym object
      const updatedGym = new Gym(gymId, {
        name: gymName.trim(),
        imageUrl: imageUrl.trim() || undefined,
        logoUrl: logoUrl.trim() || undefined,
        styles: styles,
        climbing_routes: gym.climbing_routes
      });
      
      // Save to Firestore
      await updateGym(updatedGym);
      
      setSuccess('Gym updated successfully');
      setGym(updatedGym);
      
      // Navigate back after successful save
      navigate(-1);
      
    } catch (err: any) {
      console.error('Error updating gym:', err);
      setError('Failed to update gym. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddRoute = () => {
    if (gymId) {
      navigate(`/gyms/${gymId}/routes/add`);
    }
  };

  const handleEditRoute = (routeId: string) => {
    if (gymId) {
      navigate(`/gyms/${gymId}/routes/${routeId}/edit`);
    }
  };

  const getStyleLabel = (style: string) => {
    switch (style) {
      case 'bouldering': return 'Boulder';
      case 'sport': return 'Sport';
      case 'trad': return 'Trad';
      case 'top-rope': return 'Top Rope';
      default: return style;
    }
  };

  if (loading) {
    return (
      <div className="gym-edit-container">
        <div className="gym-edit-header">
          <h1>Manage Gym</h1>
          <div className="header-actions">
            <HamburgerMenu />
          </div>
        </div>
        <div className="loading-indicator">Loading gym data...</div>
      </div>
    );
  }

  if (!hasEditAccess) {
    return (
      <div className="gym-edit-container">
        <div className="gym-edit-header">
          <h1>Manage Gym</h1>
          <div className="header-actions">
            <HamburgerMenu />
          </div>
        </div>
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You do not have permission to manage this gym.</p>
          <p>Only gym owners and administrators can edit gym details.</p>
          <button 
            className="back-button"
            onClick={() => navigate('/gyms')}
          >
            Return to Gyms
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="gym-edit-container">
      <div className="gym-edit-header">
        <h1>Manage Gym</h1>
        <div className="header-actions">
          <HamburgerMenu />
        </div>
      </div>
      <h2 className="gym-name-subtitle">{gymName}</h2>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <form className="gym-edit-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="gymName">Gym Name *</label>
          <input
            type="text"
            id="gymName"
            value={gymName}
            onChange={(e) => setGymName(e.target.value)}
            placeholder="Enter gym name"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="imageUrl">Image URL</label>
          <input
            type="url"
            id="imageUrl"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            placeholder="https://example.com/image.jpg (optional)"
          />
          {imageUrl && (
            <div className="image-preview">
              <img 
                src={imageUrl} 
                alt="Gym preview" 
                onError={(e) => {
                  e.currentTarget.src = 'https://via.placeholder.com/150?text=Invalid+URL';
                }}
              />
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="logoUrl">Logo URL</label>
          <input
            type="url"
            id="logoUrl"
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
            placeholder="https://example.com/logo.jpg (optional)"
          />
          {logoUrl && (
            <div className="logo-preview">
              <img 
                src={logoUrl} 
                alt="Logo preview" 
                onError={(e) => {
                  e.currentTarget.src = 'https://via.placeholder.com/100?text=Invalid+URL';
                }}
              />
            </div>
          )}
        </div>
        
        <div className="form-group">
          <label>Gym Styles</label>
          <div className="styles-checkboxes">
            {availableStyles.map((style) => (
              <div key={style} className="style-checkbox">
                <input
                  type="checkbox"
                  id={`style-${style}`}
                  checked={styles.includes(style)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setStyles([...styles, style]);
                    } else {
                      setStyles(styles.filter(s => s !== style));
                    }
                  }}
                />
                <label htmlFor={`style-${style}`}>{style}</label>
              </div>
            ))}
          </div>
        </div>

        <div className="form-actions">
          <button 
            type="button" 
            className="cancel-button"
            onClick={() => navigate(-1)}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="save-button"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MyGymDetail;